(() => {
  const TARGET_PRODUCT_ID = 15631749153112; // Shopify product.id
  const TARGET_VARIANT_ID = null; // optional: set if you want to match by variant id too
  const DISCOUNT_CODE = "MULEPOSE181OFF";

  const CART_SECTIONS = ["cart-drawer", "cart-icon-bubble"]; // must match section filenames
  const root = window.Shopify?.routes?.root || "/";

  const DEBUG = true;
  const log = (...a) => DEBUG && console.log("[upsell-discount]", ...a);
  const group = (name) => DEBUG && console.groupCollapsed(`[upsell-discount] ${name}`);
  const groupEnd = () => DEBUG && console.groupEnd();

  log("Loaded", { TARGET_PRODUCT_ID, TARGET_VARIANT_ID, DISCOUNT_CODE, root });

  async function getCart() {
    const res = await fetch(`${root}cart.js`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`cart.js failed: ${res.status}`);
    return res.json();
  }

  function cartSummary(cart) {
    return {
      currency: cart.currency,
      item_count: cart.item_count,
      items_subtotal_price: cart.items_subtotal_price,
      total_discount: cart.total_discount,
      total_price: cart.total_price,
      discount_codes: cart.discount_codes,
      items: (cart.items || []).map((i) => ({
        key: i.key,
        title: i.title,
        product_id: i.product_id,
        variant_id: i.id,
        quantity: i.quantity,
        final_line_price: i.final_line_price,
        original_line_price: i.original_line_price,
      })),
    };
  }

  function findTargetLine(cart) {
    const items = cart.items || [];
    return items.find((i) => {
      const pidMatch = Number(i.product_id) === Number(TARGET_PRODUCT_ID);
      const vidMatch = TARGET_VARIANT_ID ? Number(i.id) === Number(TARGET_VARIANT_ID) : false;
      return pidMatch || vidMatch;
    });
  }

  function cartHasDiscount(cart) {
    const codes = (cart.discount_codes || []).map((d) => (d.code || "").toUpperCase());
    return codes.includes(DISCOUNT_CODE.toUpperCase());
  }

  async function applyDiscountAndRender() {
    group("POST /cart/update.js (apply discount)");

    const payload = {
      discount: DISCOUNT_CODE,
      sections: CART_SECTIONS,
      sections_url: "/cart",
    };

    log("Request payload:", payload);

    const res = await fetch(`${root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      log("Response error text:", text);
      groupEnd();
      throw new Error(`cart/update.js failed: ${res.status}`);
    }

    const data = await res.json();
    log("Response discount_codes:", data.discount_codes);

    // Re-render (Shrine often has renderContents like Dawn)
    const cartDrawerEl = document.querySelector("cart-drawer");
    if (cartDrawerEl && typeof cartDrawerEl.renderContents === "function") {
      log("Rendering via cart-drawer.renderContents()");
      cartDrawerEl.renderContents(data);
      groupEnd();
      return;
    }

    // Fallback: swap section HTML if present
    if (data?.sections) {
      if (data.sections["cart-drawer"]) {
        const drawer =
          document.querySelector("#CartDrawer") ||
          document.querySelector("#cart-drawer") ||
          document.querySelector("cart-drawer");
        log("Rendering via HTML swap (cart-drawer). Found:", !!drawer);
        if (drawer) drawer.innerHTML = data.sections["cart-drawer"];
      }
      if (data.sections["cart-icon-bubble"]) {
        const bubble = document.querySelector("#cart-icon-bubble");
        log("Rendering via HTML swap (cart-icon-bubble). Found:", !!bubble);
        if (bubble) bubble.innerHTML = data.sections["cart-icon-bubble"];
      }
    }

    groupEnd();
  }

  let pending = false;
  let running = false;

  async function runOnce() {
    if (running) return;
    running = true;

    try {
      group("Run flow");

      // 1) snapshot before
      const before = await getCart();
      log("Cart BEFORE:", cartSummary(before));

      // 2) wait until target exists (race-proof)
      const start = Date.now();
      let cart = before;

      while (Date.now() - start < 6000) {
        const line = findTargetLine(cart);
        if (line) {
          log("Target line found:", {
            product_id: line.product_id,
            variant_id: line.id,
            title: line.title,
            quantity: line.quantity,
          });
          break;
        }

        await new Promise((r) => setTimeout(r, 250));
        cart = await getCart();
      }

      const targetLine = findTargetLine(cart);
      if (!targetLine) {
        log("Timeout: target product not found in cart. Final cart snapshot:", cartSummary(cart));
        groupEnd();
        return;
      }

      // 3) apply discount if not already in cart (still useful to apply even if present)
      if (!cartHasDiscount(cart)) {
        log("Discount not present in cart yet. Applying…");
      } else {
        log("Discount already present in cart. Re-applying anyway to refresh sections…");
      }

      await applyDiscountAndRender();

      // 4) snapshot after
      const after = await getCart();
      log("Cart AFTER:", cartSummary(after));

      const applied = (after.discount_codes || []).find(
        (d) => (d.code || "").toUpperCase() === DISCOUNT_CODE.toUpperCase()
      );

      if (applied) {
        log("Discount status:", applied);
        if (applied.applicable === false) {
          log(
            "Discount is NOT applicable. This points to discount rules (min spend/qty, product scope, customer eligibility, combination rules, active dates, etc.)"
          );
        }
      } else {
        log("Discount code not present in cart AFTER update (unexpected).");
      }

      groupEnd();
    } catch (e) {
      console.error("[upsell-discount] error:", e);
    } finally {
      running = false;
      pending = false;
    }
  }

  // Detect “upsell add” via click on the upsell controls (works even if no native submit happens)
  function isUpsellActionClick(target) {
    return !!target.closest(
      ".upsell__add-btn, .upsell-toggle-btn, .upsell__plus-btn, .upsell__checkbox, .upsell__toggle-switch"
    );
  }

  document.addEventListener(
    "click",
    (e) => {
      if (!isUpsellActionClick(e.target)) return;

      const upsell = e.target.closest("[data-product-id]");
      if (!upsell) return;

      const clickedProductId = Number(upsell.dataset.productId);
      const clickedVariantId = Number(upsell.dataset.id); // you set data-id="{{ product.first_available_variant.id }}"

      group("Click detected");
      log("Clicked upsell dataset:", {
        dataProductId: upsell.dataset.productId,
        dataIdVariant: upsell.dataset.id,
        clickedProductId,
        clickedVariantId,
      });
      groupEnd();

      const match =
        clickedProductId === Number(TARGET_PRODUCT_ID) ||
        (TARGET_VARIANT_ID && clickedVariantId === Number(TARGET_VARIANT_ID));

      if (!match) return;

      pending = true;

      // Let Shrine finish its own add flow first
      setTimeout(() => pending && runOnce(), 0);
    },
    true
  );

  // Extra safety: also listen for real submits from your upsell product-form if they happen
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const wrapper = form.closest('product-form[data-is-cart-upsell="true"]');
      if (!wrapper) return;

      const upsell = form.closest("[data-product-id]");
      const submitProductId = Number(upsell?.dataset?.productId);
      const submitVariantId = Number(upsell?.dataset?.id);
      const hiddenVariantInput = form.querySelector('input[name="id"]')?.value;

      group("Submit detected");
      log("Submit ids:", {
        submitProductId,
        submitVariantId,
        hiddenVariantInput,
      });
      groupEnd();

      const match =
        submitProductId === Number(TARGET_PRODUCT_ID) ||
        (TARGET_VARIANT_ID && Number(hiddenVariantInput) === Number(TARGET_VARIANT_ID));

      if (!match) return;

      pending = true;
      setTimeout(() => pending && runOnce(), 0);
    },
    true
  );
})();